/**
 * BrandVideoHero — renders the brand video in the landing page hero section.
 * AC3/4/5: autoPlay muted loop playsInline, mute toggle, FMA disclaimer.
 * Renders nothing when brand video is unavailable or GCS not configured.
 */
import { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Volume2, VolumeX } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8001';

interface BrandVideoData {
  available: boolean;
  url?: string;
  title?: string;
  duration_sec?: number;
}

async function fetchBrandVideo(): Promise<BrandVideoData> {
  const res = await fetch(`${API_BASE}/api/v1/knowledge/brand-video`);
  if (!res.ok) throw new Error('brand-video fetch failed');
  return res.json();
}

const FMA_DISCLAIMER =
  'Marome Investments (Pty) Ltd is an authorised FSP. Past performance is not indicative of future results.';

const BrandVideoHero: React.FC = () => {
  const [muted, setMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  const { data } = useQuery<BrandVideoData>({
    queryKey: ['brand-video'],
    queryFn: fetchBrandVideo,
    staleTime: 3_600_000, // 1 hour
    retry: false,
  });

  if (!data?.available || !data.url) return null;

  const handleMuteToggle = () => {
    setMuted((m) => {
      if (videoRef.current) videoRef.current.muted = !m;
      return !m;
    });
  };

  return (
    <div className="relative w-full h-full overflow-hidden rounded-2xl">
      <video
        ref={videoRef}
        src={data.url}
        autoPlay
        muted={muted}
        loop
        playsInline
        className="w-full h-full object-cover"
      />
      {/* Mute toggle — top-right */}
      <button
        onClick={handleMuteToggle}
        className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 z-10 transition-colors"
        aria-label={muted ? 'Unmute video' : 'Mute video'}
      >
        {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
      </button>
      {/* FMA disclaimer — persistent, bottom bar */}
      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs px-4 py-2 z-10">
        {FMA_DISCLAIMER}
      </div>
    </div>
  );
};

export default BrandVideoHero;
